import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppStateInterface } from 'app/core/app-config/state/models';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { FeatureCode, FeatureInterface, PermissionCode } from '../models';
import { LoadFeatures } from '../state/actions';
import { FeatureStateInterface } from '../state/models';

@Injectable({
  providedIn: 'root',
})
export class FeatureControlService {
  constructor(private readonly _store: Store<AppStateInterface>) {}

  /**
   * Dispatches action to load features from the API
   */
  public loadFeatures(): void {
    this._store.dispatch(new LoadFeatures());
  }

  /**
   * Gets all features from the store
   * @returns Observable array of features
   */
  public getFeatures(): Observable<FeatureInterface[]> {
    return this._store.select(
      ({ featureControl }: { featureControl: FeatureStateInterface }) =>
        featureControl && featureControl.features
    ).pipe(
      filter((features: FeatureInterface[]) => features !== undefined)
    );
  }

  /**
   * Checks if a feature is active
   * @param featureCode The feature code to check
   * @returns Observable boolean indicating if the feature is active
   */
  public isFeatureActive(featureCode: FeatureCode): Observable<boolean> {
    return this.getFeatures().pipe(
      map((features: FeatureInterface[]) => {
        const feature = features.find(
          (f: FeatureInterface) => f.featureCode === featureCode
        );
        return feature ? feature.isActive : false;
      })
    );
  }

  /**
   * Checks if a specific permission is enabled for a feature
   * @param featureCode The feature code
   * @param permissionCode The permission code to check
   * @returns Observable boolean indicating if the permission is enabled
   */
  public isPermissionEnabled(
    featureCode: FeatureCode,
    permissionCode: PermissionCode
  ): Observable<boolean> {
    return this.getFeatures().pipe(
      map((features: FeatureInterface[]) => {
        const feature = features.find(
          (f: FeatureInterface) => f.featureCode === featureCode
        );
        if (!feature || !feature.isActive) {
          return false;
        }
        const permission = feature.permissions.find(
          (p) => p.permissionCode === permissionCode
        );
        return permission ? permission.isEnabled : false;
      })
    );
  }

  /**
   * Gets a specific feature by its code
   * @param featureCode The feature code
   * @returns Observable of the feature or undefined
   */
  public getFeature(featureCode: FeatureCode): Observable<FeatureInterface | undefined> {
    return this.getFeatures().pipe(
      map((features: FeatureInterface[]) =>
        features.find((f: FeatureInterface) => f.featureCode === featureCode)
      )
    );
  }

  /**
   * Checks if features have been loaded
   * @returns Observable boolean
   */
  public areFeaturesLoaded(): Observable<boolean> {
    return this._store.select(
      ({ featureControl }: { featureControl: FeatureStateInterface }) =>
        featureControl && featureControl.loaded
    ).pipe(
      map((loaded: boolean) => loaded === true)
    );
  }
}
